import { useMemo, useState } from 'react'
import { RenderTags } from './components/render-tags'
import { RenderInput } from './components/render-input'
import { InputAutocomplete } from './InputSearch.styles'
import { removeQuotes } from '@utils/functions/normalizers/remove_quotes'
import { parseKeywordsToInput } from '@utils/functions/keyword/serialize'
import { Box, IconButton, Stack, SxProps } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'

import {
  AdvancedSearchType,
  KeywordConditionsType,
} from '@services/nomos_api/entities/advanced_search'
import colors from '@theme/colors'

export type MultiSelectAdvancedOption = {
  value: string
  kind: 'and' | 'or' | 'not'
}

type InputSearchProps = {
  label?: string
  placeholder?: string
  value?: KeywordConditionsType
  size?: 'medium' | 'small'
  disabled?: boolean
  disableTooltip?: boolean
  sx?: SxProps
  onChangeSearch?: (search: AdvancedSearchType) => void
  onExecuteSearch?: () => void
}

function InputSearch({
  placeholder,
  label,
  value = { or: [], and: [], not: [] },
  size = 'medium',
  disabled = false,
  disableTooltip,
  sx,
  onChangeSearch,
  onExecuteSearch,
}: InputSearchProps): JSX.Element {
  const [tempValue, setTempValue] = useState<string>('')

  const handleUpdateKeyword = (keywords: MultiSelectAdvancedOption[]) => {
    if (!onChangeSearch) return
    const search: AdvancedSearchType = keywords.reduce(
      (advancedSearch: AdvancedSearchType, item: MultiSelectAdvancedOption) => {
        advancedSearch.keywords[item.kind].push(removeQuotes(item.value))
        return advancedSearch
      },
      { keywords: { or: [], and: [], not: [] } }
    )
    onChangeSearch(search)
  }

  const parsedValue = useMemo(() => {
    return parseKeywordsToInput(value)
  }, [value])

  const disableInput = useMemo(
    () =>
      disabled ||
      parsedValue.some((keyword) => ['and', 'not'].includes(keyword.kind)),
    [disabled, parsedValue]
  )

  return (
    <Stack
      direction="row"
      flex={1}
      sx={{
        ...sx,
        justifyContent: 'space-between',
        border: 'solid 1px #0000003b',
        borderRadius: '4px',
        fieldset: {
          border: 'none',
        },
        '& .MuiFormControl-root': {
          borderRadius: '8px',
        },
      }}
    >
      <InputAutocomplete
        disabled={disableInput}
        multiple
        id="global-search-appbar"
        value={parsedValue}
        size={size}
        options={[] as MultiSelectAdvancedOption[]}
        autoComplete={false}
        ListboxComponent={Box}
        freeSolo
        selectOnFocus
        clearOnBlur
        sx={{ minWidth: 273, width: '100%' }}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onKeyDown={(event: any) => {
          if (event.key === 'Enter') {
            // Prevent's default 'Enter' behavior.
            if (tempValue === '') {
              // eslint-disable-next-line no-param-reassign
              event.defaultMuiPrevented = true
              if (onExecuteSearch) onExecuteSearch()
            }
          }
        }}
        onChange={(_, values) => {
          const options = values as Array<MultiSelectAdvancedOption | string>
          setTempValue('')
          const keywords = (options || [])?.reduce(
            (
              keywords: MultiSelectAdvancedOption[],
              value: MultiSelectAdvancedOption | string
            ) => {
              if (typeof value === 'string') {
                String(value)
                  .split(';')
                  .filter((value) => value.length > 0)
                  .forEach((value) =>
                    keywords.push({ value: value.trim(), kind: 'or' })
                  )
              } else {
                keywords.push(value as unknown as MultiSelectAdvancedOption)
              }
              return keywords
            },
            []
          )
          handleUpdateKeyword(keywords)
        }}
        renderInput={(params) => (
          <RenderInput
            params={params}
            label={label}
            disabled={disableInput}
            disableTooltip={disableTooltip}
            placeholder={disableInput ? undefined : placeholder}
            onChangeTextSearch={(value) => {
              setTempValue(value)
            }}
          />
        )}
        renderTags={(values, getTagProps) => (
          <RenderTags
            getTagProps={getTagProps}
            values={values as MultiSelectAdvancedOption[]}
            shouldRenderConnectors={disableInput}
          />
        )}
      />
      <IconButton
        data-cy="appbar-search-button"
        sx={{ alignSelf: 'center', width: '40px', height: '40px' }}
        onClick={() => {
          if (onExecuteSearch) onExecuteSearch()
        }}
      >
        <SearchIcon
          sx={{
            width: '24px',
            height: '24px',
            color: colors['$color-border-tertiary'],
          }}
        />
      </IconButton>
    </Stack>
  )
}

export default InputSearch
